<div fxLayout="column" fxLayoutGap="15px" *ngIf="selectedSpace">
  <!-- Unsaved Changes Warning & Action Buttons -->
  @if (dirty) {
    <app-info-panel class="pt-10"
                    [message]="['There are unsaved changes']"
                    [fullWidth]="true"
                    [type]="PanelType.WARN"
                    [buttons]="true"
                    [buttonLabels]="['Cancel', 'Update']"
                    [buttonDisabled]="[false, !isFormValid]"
                    (emitEvent)="$event===1 ? saveDefaultPricing() : cancelChanges()">
    </app-info-panel>
  }

  <!-- Default Pricing Section -->
  <div class="info-card-dark" fxLayout="column" fxLayoutGap="15px" [formGroup]="defaultPricingForm">
    <div fxLayout="column" fxLayoutGap="5px">
      <span class="heading-regular">Default Pricing</span>
      <span class="body-2-regular">
        Set base rates for this space by period. These will be used as defaults when creating new leases.
      </span>
    </div>

    <div formArrayName="periods" fxLayout="column" fxLayoutGap="40px">
      <div *ngFor="let _ of getPeriodsControls(); let pi = index" [formGroupName]="pi" fxLayout="column">
        <!-- Period Header -->
        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="6px">
          <mat-icon>schedule</mat-icon>
          <span class="heading-regular">{{ getPeriodLabel(pi) }}</span>
        </div>

        <div fxLayout="column" fxLayoutGap="30px" fxFlexOffset="10px" class="pl-10 pr-10" formArrayName="rates">

			<!-- Fixed Rates -->
			<div fxLayout="column" fxLayoutGap="15px">
				<span class="body-1-default">Fixed Rates</span>

				<ng-container *ngFor="let rate of getRatesControls(pi); let ri = index; let last = last;" [formGroupName]="ri">
					<ng-container *ngIf="isFixed(rate)">
						<div fxLayout="row" fxLayoutGap="10px" fxLayoutAlign="start center">
							<mat-form-field appearance="outline" class="custom-text-field" fxFlex floatLabel="always" [class.mb-10]="!last">
								<mat-label>{{ getFixedLabel(rate.get('category')?.value) }}</mat-label>
								<input formControlName="value"
									   matInput
									   type="number"
									   min="0"
									   step="0.01"
									   placeholder="0.00"
									   [disabled]="selectedSpace.locked || false"
									   (input)="onFixedValueInput(pi, ri)">
								<mat-hint>
									{{ rate.get('category')?.value === 'PER_SQM' ? 'Fixed rental per sqm' : 'Fixed rental for period' }}
								</mat-hint>
							</mat-form-field>

							<mat-button-toggle-group
								[value]="rate.get('isDefault')?.value ? [true] : []"
								(change)="onFixedDefaultToggle(pi, ri, $event)"
								multiple
								hideMultipleSelectionIndicator
								[disabled]="(selectedSpace.locked || false) || !(rate.get('value')?.value > 0)"
								class="custom-labeled-button-toggle">
								<mat-button-toggle [value]="true">
									<span>Default</span>
								</mat-button-toggle>
							</mat-button-toggle-group>
						</div>
					</ng-container>
				</ng-container>
			</div>

			<!-- Variable Rate -->
			<div fxLayout="column" fxLayoutGap="15px">
				<span class="body-1-default">Variable Rate</span>

				<ng-container *ngFor="let rate of getRatesControls(pi); let ri = index; let last = last;" [formGroupName]="ri">
					<ng-container *ngIf="isVariable(rate)">
						<div fxLayout="row" fxLayoutGap="10px" fxLayoutAlign="start center">
							<mat-form-field appearance="outline" class="custom-text-field" fxFlex floatLabel="always" [class.mb-10]="!last">
								<mat-label>Percentage</mat-label>
								<input formControlName="value"
									   matInput
									   type="number"
									   min="0"
									   max="100"
									   step="0.01"
									   placeholder="0.00"
									   [disabled]="selectedSpace.locked || false"
									   (input)="onVariableValueInput(pi)">
								<span matSuffix class="pr-10">%</span>
								<mat-hint>Variable rate (% turnover)</mat-hint>
							</mat-form-field>

							<mat-button-toggle-group
								[value]="rate.get('isDefault')?.value ? [true] : []"
								(change)="onVariableDefaultToggle(pi, $event)"
								multiple
								hideMultipleSelectionIndicator
								[disabled]="(selectedSpace.locked || false) || !(rate.get('value')?.value > 0)"
								class="custom-labeled-button-toggle">
								<mat-button-toggle [value]="true">
									<span>Default</span>
								</mat-button-toggle>
							</mat-button-toggle-group>
						</div>
					</ng-container>
				</ng-container>
			</div>

          <!-- Default Chips Summary -->
          <div fxLayout="column" fxLayoutGap="15px"
               style="padding: 12px; background-color: rgba(0, 123, 255, 0.05); border-radius: 6px; border-left: 3px solid #007bff; margin-top: 25px;">
            <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px">
              <mat-icon style="color: #007bff; font-size: 16px;">star</mat-icon>
              <div fxLayout="column">
                <span class="body-1-regular" style="font-weight: 500; font-size: 13px;">Default Options</span>
                <span class="body-2-regular" style="color: #888; font-size: 11px;">
                  Selected default rates for this period
                </span>
              </div>
            </div>

            <div fxLayout="row" fxLayoutGap="8px" fxLayoutAlign="start center" style="flex-wrap: wrap;">
              <mat-chip-set>
                <!-- Fixed default chip -->
                <mat-chip *ngIf="getSelectedFixedDefaultLabel(pi)"
                          (removed)="removeFixedDefault(pi)" [removable]="!selectedSpace.locked">
                  {{ getSelectedFixedDefaultLabel(pi) }}
                  <mat-icon matChipRemove *ngIf="!selectedSpace.locked">cancel</mat-icon>
                </mat-chip>

                <!-- Variable default chip -->
                <mat-chip *ngIf="hasVariableDefault(pi)"
                          (removed)="removeVariableDefault(pi)" [removable]="!selectedSpace.locked">
                  Variable Rate
                  <mat-icon matChipRemove *ngIf="!selectedSpace.locked">cancel</mat-icon>
                </mat-chip>
              </mat-chip-set>

              <span *ngIf="!getSelectedFixedDefaultLabel(pi) && !hasVariableDefault(pi)"
                    class="body-2-regular" style="color: #888; font-style: italic;">
                No default rates selected
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
